/**
 * AVBP Contact Spine (AD-039, 2026-06-24) — the PURE contact matcher.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  SINGLE SOURCE OF TRUTH for "is this enquiry an existing contact?"
 * ─────────────────────────────────────────────────────────────────────────
 *  PURE — NO Firestore imports, NO React. Lives in `lib/` so the Node native
 *  test runner exercises it directly under `.cjs`, and so it is reused by
 *  three callers with identical semantics:
 *    1. lead-create (Leads.tsx) — match at creation, auto-link or suggest.
 *    2. convert (Leads.tsx) — re-match once at convert time.
 *    3. the backfill script (scripts/backfill_contact_spine.cjs).
 *
 *  TUNING RULE (owner-locked 2026-06-24): a lead AUTO-links to an existing
 *  contact ONLY on an EXACT phone or EXACT email match. Name — even an exact
 *  token-sorted name match — is NEVER an auto-link; it only ever SUGGESTS
 *  (the staff-confirm chip). A two-way tie at the auto threshold downgrades
 *  to a suggestion (ambiguous → never a silent wrong link). `AUTO_LINK_
 *  THRESHOLD` (0.9) and `SUGGEST_THRESHOLD` (0.3) are the ONLY knobs — do not
 *  add fuzzy-name auto-linking in Stage 0. A false merge is worse than a miss.
 *
 *  NOTE — there is a separate `phoneKey` in `projectStore.ts` (last-9-digit,
 *  for the legacy `matchLeadToClient` chip). This module's `phoneKey` is the
 *  Stage-0 canonical (last-10-digit). They are deliberately independent; this
 *  PURE module owns the spine matcher and must not import the impure store.
 * ─────────────────────────────────────────────────────────────────────────
 */

export interface MatchInput {
  name?: string;
  phone?: string;
  email?: string;
}

export interface ContactLite {
  /** = the clients/{cid} doc id. */
  cid: string;
  clientNo: string;
  name: string;
  /** Legacy single-value fields (LiveClient.phone / .email). */
  phone?: string;
  email?: string;
  /** Person-Data-Hub multi-value fields (AD-008). */
  phones?: Array<{ number: string }>;
  emails?: Array<{ address: string }>;
}

export interface MatchResult {
  /** = ContactLite.cid (the clients/ doc id). */
  contactId: string;
  clientNo: string;
  name: string;
  /** 0..1 confidence. */
  score: number;
  /** Which signals fired: 'phone-exact' | 'email-exact' | 'name-exact' | 'phone-tail'. */
  reasons: string[];
}

/** last-10-digit phone key — tolerant to +91 / 0 / spaces / dashes / parens. */
export function phoneKey(p?: string): string {
  const d = (p ?? '').replace(/\D/g, '');
  return d.length > 10 ? d.slice(-10) : d; // 98765 43210 → 9876543210
}

export function emailKey(e?: string): string {
  return (e ?? '').trim().toLowerCase();
}

export function nameKey(n?: string): string {
  return (n ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
}

/** Token-sort name compare so "Anita Sharma" === "Sharma Anita". */
export function nameTokens(n?: string): string {
  return nameKey(n).split(' ').filter(Boolean).sort().join(' ');
}

const AUTO_LINK_THRESHOLD = 0.9; // ≥ → may stamp automatically
const SUGGEST_THRESHOLD = 0.3; //   ≥ → show the staff-confirm chip

/**
 * Score one contact against the enquiry input. Returns null below the
 * SUGGEST threshold (so the matcher never surfaces noise). Phone-exact = 1.0,
 * email-exact = 0.9, name-exact = 0.5 (suggest-only), name+phone-tail = 0.6.
 */
export function scoreContact(input: MatchInput, c: ContactLite): MatchResult | null {
  const reasons: string[] = [];
  let score = 0;

  const inPhone = phoneKey(input.phone);
  const cPhones = [c.phone, ...(c.phones ?? []).map((p) => p.number)]
    .map(phoneKey)
    .filter(Boolean);
  if (inPhone && cPhones.includes(inPhone)) {
    score = Math.max(score, 1.0);
    reasons.push('phone-exact');
  }

  const inEmail = emailKey(input.email);
  const cEmails = [c.email, ...(c.emails ?? []).map((e) => e.address)]
    .map(emailKey)
    .filter(Boolean);
  if (inEmail && cEmails.includes(inEmail)) {
    score = Math.max(score, 0.9);
    reasons.push('email-exact');
  }

  const inName = nameTokens(input.name);
  if (inName && nameTokens(c.name) === inName) {
    // Name alone is weak — only a SUGGEST signal, never auto-link.
    score = Math.max(score, 0.5);
    reasons.push('name-exact');
  }
  // Name + partial phone tail (last 7) lifts a suggestion (but still < auto).
  if (
    reasons.includes('name-exact') &&
    inPhone &&
    cPhones.some((p) => p.slice(-7) === inPhone.slice(-7))
  ) {
    score = Math.max(score, 0.6);
    reasons.push('phone-tail');
  }

  if (score < SUGGEST_THRESHOLD) return null;
  return { contactId: c.cid, clientNo: c.clientNo, name: c.name, score, reasons };
}

/** Score every contact, drop nulls, sort highest-confidence first. */
export function matchContacts(input: MatchInput, contacts: ContactLite[]): MatchResult[] {
  return contacts
    .map((c) => scoreContact(input, c))
    .filter((r): r is MatchResult => r !== null)
    .sort((a, b) => b.score - a.score);
}

/**
 * The auto-link decision. Returns the single contact safe to auto-stamp, or
 * null when nothing is confident enough OR the top two both clear the auto
 * threshold (ambiguous tie → downgrade to a suggestion).
 */
export function bestAutoLink(results: MatchResult[]): MatchResult | null {
  const top = results[0];
  if (!top || top.score < AUTO_LINK_THRESHOLD) return null;
  // Guard: do not auto-link if TWO contacts tie at the auto threshold.
  if (results[1] && results[1].score >= AUTO_LINK_THRESHOLD) return null;
  return top;
}

export { AUTO_LINK_THRESHOLD, SUGGEST_THRESHOLD };
